import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './VersionMismatchModal.css';

interface VersionMismatch {
  nodeName: string;
  nodeIP: string;
  agentVersion: string;
  kubeArmorVersion: string;
  status: 'error' | 'warning';
  recommendation: string;
}

interface VersionMismatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  mismatches: VersionMismatch[];
}

const VersionMismatchModal: React.FC<VersionMismatchModalProps> = ({
  isOpen,
  onClose,
  mismatches,
}) => {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <>
      {/* 반투명 배경 */}
      <div className="modal-overlay" onClick={onClose}></div>

      {/* 모달 내용 */}
      <div className="version-mismatch-modal">
        <div className="modal-header">
          <h2>🔧 {t('agentStatus.versionMismatchTitle') || 'Version Mismatch Details'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <p className="modal-description">
            {t('agentStatus.versionMismatchDescription') || 
             'The following nodes have version mismatches between agent and KubeArmor. Please update to maintain consistency.'}
          </p>

          <div className="mismatch-table">
            {mismatches.map((mismatch, idx) => (
              <div key={idx} className="mismatch-item">
                <div className="mismatch-node-header">
                  <div className="node-name">
                    <span className="node-icon">🖥️</span>
                    <span className="node-title">{mismatch.nodeName}</span>
                    <span className="node-ip">({mismatch.nodeIP})</span>
                  </div>
                  <div className={`status-badge status-${mismatch.status}`}>
                    {mismatch.status === 'error' ? '🚨 ERROR' : '⚠️ WARNING'}
                  </div>
                </div>

                <div className="version-info">
                  <div className="version-row">
                    <label>Agent Version:</label>
                    <span className="version-agent">{mismatch.agentVersion}</span>
                  </div>
                  <div className="version-row">
                    <label>KubeArmor Version:</label>
                    <span className="version-kubearmor">{mismatch.kubeArmorVersion}</span>
                  </div>
                  <div className="mismatch-indicator">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="9" stroke="#ff4444" strokeWidth="2" />
                      <text x="10" y="13" fontSize="12" fill="#ff4444" textAnchor="middle">✕</text>
                    </svg>
                  </div>
                </div>

                <div className="recommendation">
                  <strong>💡 Recommendation:</strong>
                  <p>{mismatch.recommendation}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-info-box">
            <strong>ℹ️ How to resolve:</strong>
            <ul>
              <li>Update the KubeArmor agent on affected nodes to match the cluster version</li>
              <li>Use <code>kubectl patch daemonset kubearmor -p '...'</code> to update</li>
              <li>Or manually update each node and restart the agent</li>
              <li>Monitor the Agent Status page for confirmation</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-close" onClick={onClose}>Close</button>
          <button className="btn-view-agents" onClick={() => window.location.href = '/analysis/agent-status'}>
            View All Agents →
          </button>
        </div>
      </div>
    </>
  );
};

export default VersionMismatchModal;
